import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { useAppSelector } from "./types";
import statsApi, { StatsRequest } from "@/api/stats";
import { getUserDetails } from "@/store/user/selectors";

// Cache configuration - optimized for dashboard
const DASHBOARD_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const DASHBOARD_STALE_TIME = 2 * 60 * 1000; // 2 minutes

export interface DashboardDataParams {
    dateRange: DateRange;
    userId: string;
}

export const useDashboardData = (dateRange: DateRange) => {
    const userDetails = useAppSelector(getUserDetails);

    const params: StatsRequest = useMemo(
        () => ({
            startDate: dateRange.from?.toISOString() || null,
            endDate: dateRange.to?.toISOString() || null,
            userId: userDetails?.id || "",
        }),
        [dateRange, userDetails?.id],
    );

    // Quick Stats - combines multiple endpoints efficiently
    const quickStatsQuery = useQuery({
        queryKey: ["dashboard", "quickStats", params],
        queryFn: async () => {
            const [users, transactions, resumes, userJobs] = await Promise.all([
                statsApi.getAllUsers(params),
                statsApi.getCreditTransactionAnalytics(params),
                statsApi.getAllResumes(params),
                statsApi.getAllUserJobs(params),
            ]);

            return {
                users,
                resumes,
                transactions: transactions.transactions,
                userJobs: userJobs?.results ?? [],
            };
        },
        staleTime: DASHBOARD_STALE_TIME,
        gcTime: DASHBOARD_CACHE_TIME,
        enabled: !!userDetails?.id,
    });

    // Users Analytics - uses your existing user profile stats
    // const usersAnalyticsQuery = useQuery({
    //     queryKey: ["dashboard", "usersAnalytics", params],
    //     queryFn: async () => {
    //         const [userProfiles] = await Promise.all([
    //             statsApi.getCreditSpendUserProfileStats(params),
    //         ]);

    //         return {
    //             segments: userProfiles,
    //         };
    //     },
    //     staleTime: DASHBOARD_STALE_TIME,
    //     gcTime: DASHBOARD_CACHE_TIME,
    //     enabled: !!userDetails?.id,
    // });

    // Transactions Analytics - uses your existing transaction analytics
    const transactionsAnalyticsQuery = useQuery({
        queryKey: ["dashboard", "transactionsAnalytics", params],
        queryFn: async () => {
            const transactionsResponse =
                await statsApi.getCreditTransactionAnalytics(params);

            return transactionsResponse;
        },
        staleTime: DASHBOARD_STALE_TIME,
        gcTime: DASHBOARD_CACHE_TIME,
        enabled: !!userDetails?.id,
    });

    return {
        quickStats: quickStatsQuery.data,
        transactionsAnalytics: transactionsAnalyticsQuery.data,
        isLoading:
            quickStatsQuery.isLoading || transactionsAnalyticsQuery.isLoading,
        isError: quickStatsQuery.isError || transactionsAnalyticsQuery.isError,
        error: quickStatsQuery.error || transactionsAnalyticsQuery.error,
        refetch: () => {
            quickStatsQuery.refetch();
            transactionsAnalyticsQuery.refetch();
        },
    };
};
